<script setup lang="ts">
import type { AxiosResponse } from 'axios'
import {ref, type Ref, onMounted, watch, PropType} from 'vue'
import api from "@/app/services/api.ts";
import {
  ISICClass,
  ISICDivision,
  ISICGroup,
  ISICSection,
  ISICSelection
} from "@/domains/home/services/nir/forms/components/widgets/types";


const props = defineProps({
  active: Boolean,
  modelValue: {
    type: Object as PropType<ISICSelection>,
  },
  data: {
    type: Object as PropType<ISICSelection>,
  },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: ISICSelection): void
}>()

const sections: Ref<ISICSection[] | undefined> = ref()
const divisions: Ref<ISICDivision[] | undefined> = ref()
const groups: Ref<ISICGroup[] | undefined> = ref()
const classes: Ref<ISICClass[] | undefined> = ref()

const selectedSection = ref('')
const selectedDivision = ref('')
const selectedGroup = ref('')
const selectedClass = ref('')
const isicCode = ref('')

onMounted(() => {
  fetchSections()
})

async function fetchSections() {
  try {
    const response: AxiosResponse<ISICSection[]> = await api.get(`/reservation/isic/parents`)
    sections.value = response.data
    selectedSection.value = props.data?.sectionId || ""
  } catch {
    sections.value = []
  }
}

async function fetchDivisions(sectionId: string) {
  try {
    const response: AxiosResponse<ISICDivision[]> = await api.get(`/reservation/isic?parentId=${sectionId}`)
    divisions.value = response.data
    selectedDivision.value = props.data?.divisionId || ""
  } catch {
    divisions.value = []
  }
}

async function fetchGroups(divisionId: string) {
  try {
    const response: AxiosResponse<ISICGroup[]> = await api.get(`/reservation/isic?parentId=${divisionId}`)
    groups.value = response.data
    selectedGroup.value = props.data?.groupId || ""
  } catch {
    groups.value = []
  }
}

async function fetchClasses(groupId: string) {
  try {
    const response: AxiosResponse<ISICClass[]> = await api.get(`/reservation/isic?parentId=${groupId}`)
    classes.value = response.data
    selectedClass.value = props.data?.classId || ""
  } catch {
    classes.value = []
  }
}

// Function to build ISIC code from selected levels
function updateIsicCode() {
  let code = ''

  // Get section code
  if (selectedSection.value && sections.value) {
    const section = sections.value.find(s => s.id === selectedSection.value)
    if (section?.code) {
      code += section.code
    }
  }

  // Get division code
  if (selectedDivision.value && divisions.value) {
    const division = divisions.value.find(d => d.id === selectedDivision.value)
    if (division?.code) {
      code += division.code
    }
  }

  // Get group code
  if (selectedGroup.value && groups.value) {
    const group = groups.value.find(g => g.id === selectedGroup.value)
    if (group?.code) {
      code += group.code
    }
  }

  // Get class code
  if (selectedClass.value && classes.value) {
    const cls = classes.value.find(c => c.id === selectedClass.value)
    if (cls?.code) {
      code += cls.code
    }
  }

  isicCode.value = code
}

// Watch for section change
watch(selectedSection, (newValue) => {
  if (newValue) {
    // Reset dependent fields
    selectedDivision.value = ''
    selectedGroup.value = ''
    selectedClass.value = ''
    divisions.value = undefined
    groups.value = undefined
    classes.value = undefined

    // Fetch divisions for selected section
    fetchDivisions(newValue)
  }
  updateIsicCode()
  emitSelection()
})

// Watch for division change
watch(selectedDivision, (newValue) => {
  if (newValue) {
    // Reset dependent fields
    selectedGroup.value = ''
    selectedClass.value = ''
    groups.value = undefined
    classes.value = undefined

    // Fetch groups for selected division
    fetchGroups(newValue)
  }
  updateIsicCode()
  emitSelection()
})

// Watch for group change
watch(selectedGroup, (newValue) => {
  if (newValue) {
    // Reset dependent fields
    selectedClass.value = ''
    classes.value = undefined

    // Fetch classes for selected group
    fetchClasses(newValue)
  }
  updateIsicCode()
  emitSelection()
})

// Watch for class change
watch(selectedClass, () => {
  updateIsicCode()
  emitSelection()
})

function emitSelection() {
  emit('update:modelValue', {
    sectionId: selectedSection.value,
    divisionId: selectedDivision.value,
    groupId: selectedGroup.value,
    classId: selectedClass.value,
    isicCode: isicCode.value
  })
}
</script>

<template>
  <div class="flex flex-col p-3 border border-gray-200 rounded-xl">
    <div class="mb-2">
      <span class="text-xs font-semibold text-gray-600 uppercase">
        <i class="fa-solid fa-industry mr-1"></i>
        ISIC Business Classification
      </span>
    </div>
    <!-- ISIC Code Display -->
    <div v-if="isicCode" class="mb-3 p-3 bg-gradient-to-r from-primary to-primary-800 rounded-lg">
      <div class="flex items-center justify-between">
        <div>
          <span class="text-xs font-medium text-white opacity-90 uppercase tracking-wide">ISIC Code</span>
          <p class="text-2xl font-bold text-white mt-1 font-mono">{{ isicCode }}</p>
        </div>
        <i class="fa-solid fa-barcode text-white text-3xl opacity-50"></i>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-2">
      <!-- Section (Level 1) -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1.5">
          <i class="fa-solid fa-layer-group text-gray-400 mr-1"></i>
          Level 1 (Section) <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
        </label>
        <select
          v-model="selectedSection"
          :disabled="!active"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
          <option value="">Select Section</option>
          <option
            v-for="section in sections"
            :key="section.id"
            :value="section.id">
            {{ section.code }} - {{ section.description }}
          </option>
        </select>
      </div>

      <!-- Division (Level 2) -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1.5">
          <i class="fa-solid fa-sitemap text-gray-400 mr-1"></i>
          Level 2 (Division) <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
        </label>
        <select
          v-model="selectedDivision"
          :disabled="!selectedSection || !divisions || divisions.length === 0"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">{{ !selectedSection ? 'Select Section first' : 'Select Division' }}</option>
          <option
            v-for="division in divisions"
            :key="division.id"
            :value="division.id"
          >
            {{ division.code }} - {{ division.description }}
          </option>
        </select>
      </div>

      <!-- Group (Level 3) -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1.5">
          <i class="fa-solid fa-folder-tree text-gray-400 mr-1"></i>
          Level 3 (Group) <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
        </label>
        <select
          v-model="selectedGroup"
          :disabled="!selectedDivision || !groups || groups.length === 0"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">{{ !selectedDivision ? 'Select Division first' : 'Select Group' }}</option>
          <option
            v-for="group in groups"
            :key="group.id"
            :value="group.id"
          >
            {{ group.code }} - {{ group.description }}
          </option>
        </select>
      </div>

      <!-- Class (Level 4) -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1.5">
          <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
          Level 4 (Class) <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
        </label>
        <select
          v-model="selectedClass"
          :disabled="!selectedGroup || !classes || classes.length === 0"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">{{ !selectedGroup ? 'Select Group first' : 'Select Class' }}</option>
          <option
            v-for="cls in classes"
            :key="cls.id"
            :value="cls.id"
          >
            {{ cls.code }} - {{ cls.description }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
